import React from "react";

const CCY = { EUR: "€", PLN: "zł", SEK: "kr", DKK: "kr", CZK: "Kč", HUF: "Ft",
  RON: "lei", BGN: "лв", CHF: "CHF", NOK: "kr", GBP: "£" };

export const fmtMoney = (v, ccy = "EUR") => {
  if (v == null || isNaN(v)) return "—";
  const sym = CCY[ccy] || "";
  const n = Math.round(v).toLocaleString("en-US");
  return sym === "€" || sym === "£" ? `${sym}${n}` : `${n} ${sym || ccy}`;
};
export const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

const DECISION = {
  BUY: { label: "BUY", cls: "bg-emerald-600 text-white", ring: "ring-emerald-200" },
  NEGOTIATE: { label: "NEGOTIATE", cls: "bg-amber-500 text-white", ring: "ring-amber-200" },
  PASS: { label: "PASS", cls: "bg-rose-600 text-white", ring: "ring-rose-200" },
  INSUFFICIENT_DATA: { label: "INSUFFICIENT DATA", cls: "bg-slate-500 text-white", ring: "ring-slate-200" },
};
const LEVEL_CLS = {
  Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  High: "bg-rose-50 text-rose-700 border-rose-200",
};
const BASIS_CLS = {
  HUMAN_VERIFIED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ESTIMATED: "bg-amber-50 text-amber-700 border-amber-200",
  CALCULATED: "bg-sky-50 text-sky-700 border-sky-200",
  ACTUAL: "bg-violet-50 text-violet-700 border-violet-200",
  RAW: "bg-slate-100 text-slate-600 border-slate-200",
};

const Basis = ({ b }) => (
  <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm border ${BASIS_CLS[b] || BASIS_CLS.RAW}`}>{(b || "").replace("_", " ")}</span>
);

const Card = ({ title, children, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={testid}>
    {title && <h3 className="text-sm font-semibold text-atlas-ink mb-3">{title}</h3>}
    {children}
  </div>
);

const METER_TONE = { sky: "bg-sky-500", rose: "bg-rose-500", emerald: "bg-emerald-500" };
const Meter = ({ value, tone = "sky" }) => (
  <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
    <div className={`h-full ${METER_TONE[tone] || METER_TONE.sky}`} style={{ width: `${Math.max(0, Math.min(100, value || 0))}%`, transition: "width .5s" }} />
  </div>
);

export const OpportunityIntelligence = ({ data }) => {
  if (!data) return null;
  const i = data.intelligence || {};
  const l = data.landed || {};
  const acq = data.acquisition || {};
  const v = data.vehicle || {};
  const ccy = i.destCurrency || acq.destinationCurrency || "EUR";
  const d = DECISION[i.decision] || DECISION.INSUFFICIENT_DATA;
  const title = [v.make, v.model, v.version, v.year].filter(Boolean).join(" ");

  return (
    <div className="space-y-5" data-testid="opportunity-intelligence">
      {/* Decision header */}
      <div className={`rounded-xl border border-slate-200 bg-white p-6 ring-4 ${d.ring}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Atlas recommendation</div>
            <div className="flex items-center gap-3 mt-1">
              <span className={`text-lg font-bold px-3 py-1 rounded-md ${d.cls}`} data-testid="opp-decision-badge">{d.label}</span>
              <span className="text-slate-500 text-sm">{title || "Opportunity"} · {acq.sourceCountry} → {acq.destinationCountry}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Max recommended price</div>
            <div className="text-2xl font-bold text-atlas-ink font-mono" data-testid="opp-max-price">{fmtMoney(i.maxPurchasePrice, ccy)}</div>
            <div className="text-xs text-slate-400">asking {fmtMoney(acq.askingPrice, acq.askingCurrency)}</div>
          </div>
        </div>
        <ul className="mt-4 space-y-1" data-testid="opp-decision-reasons">
          {(i.decisionReasons || []).map((r, k) => (
            <li key={k} className="text-sm text-slate-600 flex gap-2"><span className="text-atlas-blue">›</span>{r}</li>
          ))}
        </ul>
      </div>

      {/* Score / Risk / Confidence */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card testid="opp-score-card">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm text-slate-500">Opportunity Score</span>
            <span className="text-2xl font-bold text-atlas-ink font-mono" data-testid="opp-score">{i.opportunityScore ?? "—"}<span className="text-sm text-slate-400">/100</span></span>
          </div>
          <Meter value={i.opportunityScore} tone="sky" />
          <ul className="mt-3 space-y-1">
            {(i.whyReasons || []).map((r, k) => <li key={k} className="text-xs text-slate-500">{r}</li>)}
          </ul>
        </Card>
        <Card testid="opp-risk-card">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm text-slate-500">Risk</span>
            <span className={`text-xs px-2 py-1 rounded-md border ${LEVEL_CLS[i.riskLevel] || LEVEL_CLS.Medium}`} data-testid="opp-risk-level">{i.riskLevel || "—"} · {i.riskScore ?? 0}</span>
          </div>
          <Meter value={i.riskScore} tone="rose" />
          <ul className="mt-3 space-y-1">
            {(i.riskReasons || []).map((r, k) => <li key={k} className="text-xs text-slate-500">• {r}</li>)}
          </ul>
        </Card>
        <Card testid="opp-confidence-card">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm text-slate-500">Confidence</span>
            <span className={`text-xs px-2 py-1 rounded-md border ${LEVEL_CLS[i.confidence] || LEVEL_CLS.Medium}`} data-testid="opp-confidence">{i.confidence || "—"}</span>
          </div>
          <Meter value={i.completeness} tone="emerald" />
          <p className="mt-3 text-xs text-slate-500">Data completeness {i.completeness}% · {i.comparableCount} comparable(s). Valuation basis: {i.destinationValueBasis || "n/a"}.</p>
        </Card>
      </div>

      {/* Economics */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Financial analysis (landed cost)" testid="opp-landed-card">
          <div className="space-y-1.5">
            {(l.lines || []).map((ln, k) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-600 flex items-center gap-2">{ln.label} <Basis b={ln.basis} /></span>
                <span className="font-mono text-slate-800">{fmtMoney(ln.amountDest, ccy)}{ln.currency !== ccy && ln.amount != null ? <span className="text-slate-400 text-xs"> ({fmtMoney(ln.amount, ln.currency)})</span> : null}</span>
              </div>
            ))}
            <div className="border-t border-slate-200 mt-2 pt-2 flex items-center justify-between">
              <span className="font-semibold text-atlas-ink flex items-center gap-2">Total landed cost <Basis b="CALCULATED" /></span>
              <span className="font-mono font-bold text-atlas-ink" data-testid="opp-landed-total">{fmtMoney(l.totalLandedCost, ccy)}</span>
            </div>
            {l.fxDate && <p className="text-[11px] text-slate-400 mt-1">FX rates ECB {l.fxDate}. {l.unconvertible?.length ? `Unconvertible: ${l.unconvertible.join(", ")}.` : ""}</p>}
          </div>
        </Card>
        <Card title="Market & margin" testid="opp-margin-card">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600 flex items-center gap-2">Destination value <Basis b={i.destinationValueBasis} /></span><span className="font-mono text-slate-800">{fmtMoney(i.destinationValue, ccy)}</span></div>
            <div className="text-[11px] text-slate-400 -mt-1">{i.destinationValueDetail}</div>
            <div className="flex justify-between"><span className="text-slate-600">Total landed cost</span><span className="font-mono text-slate-800">{fmtMoney(l.totalLandedCost, ccy)}</span></div>
            <div className="border-t border-slate-200 pt-2 flex justify-between"><span className="font-semibold text-atlas-ink">Expected gross margin</span><span className="font-mono font-bold text-atlas-ink" data-testid="opp-gross-margin">{fmtMoney(i.grossMargin, ccy)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Margin % (on sale)</span><span className="font-mono text-slate-800" data-testid="opp-margin-pct">{fmtPct(i.marginPct)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Target margin</span><span className="font-mono text-slate-500">{fmtPct(i.targetMarginPct)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-2"><span className="font-semibold text-atlas-ink">Max recommended purchase</span><span className="font-mono font-bold text-atlas-blue">{fmtMoney(i.maxPurchasePrice, ccy)}</span></div>
          </div>
        </Card>
      </div>

      {/* Comparables + vehicle */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card title={`Comparable vehicles (${(data.comparables || []).length})`} testid="opp-comparables-card">
          {(data.comparables || []).length ? (
            <div className="space-y-1.5">
              {data.comparables.map((c, k) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{[c.year, c.mileage ? `${Number(c.mileage).toLocaleString()} km` : null, c.note].filter(Boolean).join(" · ") || `Comparable ${k + 1}`}</span>
                  <span className="font-mono text-slate-800">{fmtMoney(c.price, c.currency || ccy)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No comparable vehicles entered.</p>}
        </Card>
        <Card title="Vehicle & acquisition" testid="opp-vehicle-card">
          <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
            {[["Make", v.make], ["Model", v.model], ["Year", v.year], ["Mileage", v.mileage ? `${Number(v.mileage).toLocaleString()} km` : null],
              ["Fuel", v.fuel], ["Transmission", v.transmission], ["Class", v.classification], ["Corridor", `${acq.sourceCountry} → ${acq.destinationCountry}`]]
              .map(([label, val], k) => val ? (
                <React.Fragment key={k}><dt className="text-slate-400">{label}</dt><dd className="text-slate-700 text-right">{val}</dd></React.Fragment>
              ) : null)}
          </dl>
          {v.source_listing_url && <a href={v.source_listing_url} target="_blank" rel="noreferrer" className="text-xs text-atlas-blue hover:underline mt-3 inline-block">View source listing ↗</a>}
        </Card>
      </div>
    </div>
  );
};
